/* Master Order → Расчёты → Себестоимость изделия
 * Stage-1 (components) + persistence to Workbook as `product_cost_calculation`.
 * Recipe Recalc / Recipe Cost / Workbook backend architecture unchanged.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X, Plus, Trash2, NotebookPen, Pencil, ArrowLeft, Save, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import MOShell, {
  useMOAccess, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  listWorkbookEntries, getWorkbookEntry,
  createWorkbookEntry, updateWorkbookEntry,
} from "@/lib/workbookApi";
import {
  listPackagingItems, listPackagingPricesByItem,
  listOrders, listClients,
  createProductCostSnapshot, updateProductCostSnapshot,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ─────────── shared helpers ─────────── */
const CURRENCIES = ["EUR", "USD", "RUB"];
const fmtDec = (n, digits = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(digits).replace(".", ",");
};
const parseAmount = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* ─────────── component row math ───────────
 * A component is "usable" only when it has a base_cost AND its currency
 * matches the product currency. Otherwise it renders visibly but does NOT
 * contribute to the subtotal. This mirrors the user rules 5 and 6. */
const isUsable = (comp, productCurrency) =>
  Number.isFinite(comp.base_cost) && comp.base_cost !== null
  && comp.currency === productCurrency;

const componentTotal = (comp) => {
  if (!Number.isFinite(comp.base_cost)) return null;
  const coef = Number(comp.coefficient);
  if (!Number.isFinite(coef) || coef <= 0) return null;
  return comp.base_cost * coef;
};

/* Packaging line math mirrors component math but on quantity × unit_cost.
 * Both sides may be null / invalid — that's what the "цена не указана"
 * hint communicates and it's excluded from the subtotal.  Currency also
 * has to match the product currency (no auto-conversion, per spec). */
const isPkgUsable = (row, productCurrency) =>
  Number.isFinite(row.unit_cost) && row.unit_cost !== null
  && Number.isFinite(Number(row.quantity)) && Number(row.quantity) > 0
  && row.currency === productCurrency;

const pkgLineTotal = (row) => {
  if (!Number.isFinite(row.unit_cost) || row.unit_cost === null) return null;
  const q = Number(row.quantity);
  if (!Number.isFinite(q) || q <= 0) return null;
  return row.unit_cost * q;
};

/* A tiny curated list of consumable units for manual rows. The Master Order
 * packaging catalog uses its own base_unit enum; we don't reuse it here to
 * avoid pulling backend types into a free-text UI. */
const MANUAL_PKG_UNITS = ["pcs", "m", "g", "ml"];

/* ─────────── main page ─────────── */
export default function MOProductCost() {
  const { mode, loading: accessLoading } = useMOAccess();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const openEntryId = searchParams.get("entry");
  // Sprint MO-UX-Simplify: open from an Order position with context params.
  const orderCtxId = searchParams.get("order_id");
  const [orderCtxNumber, setOrderCtxNumber] = useState("");
  useEffect(() => {
    if (!orderCtxId) return;
    (async () => {
      try {
        const o = await (await import("@/lib/masterOrderApi")).getOrder(orderCtxId);
        const n = String(o?.order_number || "").replace(/^0+/, "") || "0";
        setOrderCtxNumber(n);
      } catch { /* silent */ }
    })();
  }, [orderCtxId]);

  const [productName, setProductName] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [components, setComponents] = useState([]);
  const [packagingRows, setPackagingRows] = useState([]);
  const [laborHours, setLaborHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [salePrice, setSalePrice] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState(null); // 'choose' | 'workbook' | 'manual'
  const [pkgAddOpen, setPkgAddOpen] = useState(false);
  const [pkgAddMode, setPkgAddMode] = useState(null); // 'choose' | 'catalog' | 'manual'

  // Persistence state.
  // savedEntryId → after first save this is the workbook entry id; further
  // saves become PATCH on the same row (no duplication).
  const [savedEntryId, setSavedEntryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  // Order picker (Sprint Product-Cost-Attach).
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [attachConfirm, setAttachConfirm] = useState(null); // { order, existing }
  // Suppress the initial dirty=true burst that would fire when we hydrate
  // from an entry (state loads triggers effect → would mark loaded state
  // as unsaved changes). Ref because we don't want to re-render on flip.
  const hydratingRef = useRef(false);

  const openAdd = () => { setAddMode("choose"); setAddOpen(true); };
  const closeAdd = () => { setAddOpen(false); setAddMode(null); };

  const addComponent = (comp) => {
    setComponents((prev) => [...prev, comp]);
    closeAdd();
  };
  const removeComponent = (id) =>
    setComponents((prev) => prev.filter((c) => c.id !== id));
  const patchComponent = (id, patch) =>
    setComponents((prev) => prev.map((c) => c.id === id ? { ...c, ...patch } : c));

  // ── Packaging & consumables (Stage 2) ────────────────────────────────
  // Stored in a separate list; math is independent of components so the
  // two subtotals can be displayed cleanly.  Rules mirror Stage 1:
  //   • Missing unit_cost → row shown, "Цена не указана", excluded from sum.
  //   • Row currency must match product currency; otherwise excluded.
  //   • Total = quantity × unit_cost.
  const addPkg = (row) => { setPackagingRows((p) => [...p, row]); setPkgAddOpen(false); setPkgAddMode(null); };
  const removePkg = (id) => setPackagingRows((prev) => prev.filter((r) => r.id !== id));
  const patchPkg = (id, patch) =>
    setPackagingRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));

  const subtotal = useMemo(() => components.reduce((s, c) => {
    if (!isUsable(c, currency)) return s;
    const t = componentTotal(c);
    return s + (Number.isFinite(t) ? t : 0);
  }, 0), [components, currency]);

  const packagingSubtotal = useMemo(() => packagingRows.reduce((s, r) => {
    if (!isPkgUsable(r, currency)) return s;
    const t = pkgLineTotal(r);
    return s + (Number.isFinite(t) ? t : 0);
  }, 0), [packagingRows, currency]);

  // ── Stage 3: labor + additional expenses ──────────────────────────────
  const laborTotal = useMemo(() => {
    const h = parseAmount(laborHours);
    const r = parseAmount(hourlyRate);
    if (h === null || r === null || h <= 0 || r < 0) return null;
    return h * r;
  }, [laborHours, hourlyRate]);
  const expensesSubtotal = useMemo(() => expenses.reduce((s, e) => {
    const a = Number(e.amount);
    if (!Number.isFinite(a) || a < 0) return s;
    // Free-form expenses don't have their own currency picker — they inherit
    // the product currency, so no mismatch check needed here.
    return s + a;
  }, 0), [expenses]);
  const grandTotal = subtotal + packagingSubtotal;
  const fullCostTotal = grandTotal + (Number.isFinite(laborTotal) ? laborTotal : 0) + expensesSubtotal;

  // ── Stage 4: sale price / profit / margin ────────────────────────────
  // sale_price is the confectioner's chosen retail price (same currency as
  // the calculation). All three values are UI-derived so unfilled sale price
  // simply renders "—" without blocking save. Negative profit is allowed
  // (chef sells below cost) — we don't hide the loss, we show it honestly.
  const salePriceNum = useMemo(() => parseAmount(salePrice), [salePrice]);
  const hasSalePrice = salePriceNum !== null;
  const hasPositiveSale = hasSalePrice && salePriceNum > 0;
  const profit = hasSalePrice ? (salePriceNum - fullCostTotal) : null;
  // Margin = profit / sale_price × 100. Only computable when sale_price > 0
  // (division by zero / negative sale is meaningless).
  const marginPercent = hasPositiveSale ? (profit / salePriceNum) * 100 : null;

  // ── dirty tracking ────────────────────────────────────────────────────
  useEffect(() => {
    if (hydratingRef.current) { hydratingRef.current = false; return; }
    setDirty(true);
  }, [productName, currency, components, packagingRows, laborHours, hourlyRate, expenses, salePrice]);

  // ── load an existing entry (from ?entry=…) ────────────────────────────
  useEffect(() => {
    if (!openEntryId) return;
    let cancelled = false;
    (async () => {
      setLoadingEntry(true);
      try {
        const entry = await getWorkbookEntry(openEntryId);
        if (cancelled || !entry) return;
        if (entry.type !== "product_cost_calculation") {
          toast.error("Эту запись нельзя открыть здесь");
          return;
        }
        const p = entry.payload || {};
        hydratingRef.current = true;
        // Batch the three state writes; React 18 batches these inside a
        // microtask boundary, but we set hydratingRef so ONLY the first
        // resulting effect skips dirty=true.
        setProductName(p.title || "");
        setCurrency(p.currency_code || "EUR");
        setComponents(Array.isArray(p.components) ? p.components.map((c) => ({
          id: c.id || uid(),
          source: c.source === "workbook" ? "workbook" : "manual",
          workbook_entry_id: c.workbook_entry_id || null,
          name: c.name || "",
          base_cost: c.base_cost === null || c.base_cost === undefined ? null : Number(c.base_cost),
          currency: c.currency || (p.currency_code || "EUR"),
          coefficient: c.coefficient === null || c.coefficient === undefined ? 1 : Number(c.coefficient),
        })) : []);
        setPackagingRows(Array.isArray(p.packaging_consumables) ? p.packaging_consumables.map((r) => ({
          id: r.id || uid(),
          source: r.source === "packaging" ? "packaging" : "manual",
          packaging_item_id: r.packaging_item_id || null,
          name: r.name || "",
          quantity: r.quantity === null || r.quantity === undefined ? 1 : Number(r.quantity),
          unit: r.unit || "pcs",
          unit_cost: r.unit_cost === null || r.unit_cost === undefined ? null : Number(r.unit_cost),
          currency: r.currency || (p.currency_code || "EUR"),
        })) : []);
        setLaborHours(p.labor_hours === null || p.labor_hours === undefined ? "" : String(p.labor_hours).replace(".", ","));
        setHourlyRate(p.hourly_rate === null || p.hourly_rate === undefined ? "" : String(p.hourly_rate).replace(".", ","));
        setExpenses(Array.isArray(p.additional_expenses) ? p.additional_expenses.map((e) => ({
          id: e.id || uid(),
          name: e.name || "",
          amount: e.amount === null || e.amount === undefined ? null : Number(e.amount),
          currency: e.currency || (p.currency_code || "EUR"),
        })) : []);
        setSalePrice(p.sale_price === null || p.sale_price === undefined ? "" : String(p.sale_price).replace(".", ","));
        setSavedEntryId(entry.id);
        // The dirty effect runs after these setState calls; the ref
        // consumes the first pass so we start clean.
        setDirty(false);
      } catch (e) {
        toast.error("Не удалось открыть расчёт");
      } finally { if (!cancelled) setLoadingEntry(false); }
    })();
    return () => { cancelled = true; };
  }, [openEntryId]);

  // ── save/patch ────────────────────────────────────────────────────────
  const buildPayload = () => ({
    title: (productName || "").trim() || "Изделие",
    currency_code: currency,
    // "draft" is the only status we produce in this sprint; the schema
    // already reserves "final" for a later sprint (upstream of packaging /
    // labor / sale price) — no UI or code should set it yet.
    status: "draft",
    subtotal,
    grand_total: grandTotal,
    packaging_consumables_subtotal: packagingSubtotal,
    components: components.map((c) => ({
      id: c.id,
      source: c.source,
      workbook_entry_id: c.workbook_entry_id || null,
      name: (c.name || "").trim() || "Компонент",
      base_cost: Number.isFinite(c.base_cost) ? c.base_cost : null,
      currency: c.currency || currency,
      coefficient: Number.isFinite(Number(c.coefficient)) ? Number(c.coefficient) : 1,
    })),
    packaging_consumables: packagingRows.map((r) => ({
      id: r.id,
      source: r.source,
      packaging_item_id: r.packaging_item_id || null,
      name: (r.name || "").trim() || "Позиция",
      quantity: Number.isFinite(Number(r.quantity)) ? Number(r.quantity) : 0,
      unit: r.unit || "pcs",
      unit_cost: Number.isFinite(r.unit_cost) ? r.unit_cost : null,
      currency: r.currency || currency,
    })),
    labor_hours: parseAmount(laborHours),
    hourly_rate: parseAmount(hourlyRate),
    labor_total: laborTotal,
    additional_expenses_subtotal: expensesSubtotal,
    full_cost_total: fullCostTotal,
    sale_price: salePriceNum,
    profit: profit,
    margin_percent: marginPercent,
    additional_expenses: expenses.map((e) => ({
      id: e.id,
      name: (e.name || "").trim() || "Расход",
      amount: Number.isFinite(e.amount) ? e.amount : null,
      currency: e.currency || currency,
    })),
  });

  const validate = () => {
    if (!(productName || "").trim()) {
      toast.error("Укажите название изделия"); return false;
    }
    if (components.length === 0 && packagingRows.length === 0) {
      toast.error("Добавьте хотя бы один компонент или позицию упаковки"); return false;
    }
    return true;
  };

  const saveNew = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const created = await createWorkbookEntry({
        type: "product_cost_calculation",
        payload: buildPayload(),
      });
      setSavedEntryId(created.id);
      setDirty(false);
      toast.success("Сохранено в Рабочую тетрадь");
    } catch (e) { toast.error("Не удалось сохранить"); }
    finally { setSaving(false); }
  };

  const saveChanges = async () => {
    if (!savedEntryId) return saveNew();
    if (!validate() || saving) return;
    setSaving(true);
    try {
      await updateWorkbookEntry(savedEntryId, { payload: buildPayload() });
      setDirty(false);
      toast.success("Изменения сохранены");
    } catch (e) { toast.error("Не удалось сохранить"); }
    finally { setSaving(false); }
  };

  // ── back navigation with unsaved-changes prompt ───────────────────────
  const doBack = () => {
    if (dirty) { setLeavePromptOpen(true); return; }
    nav("/master-order");
  };
  const leaveDiscard = () => { setLeavePromptOpen(false); nav("/master-order"); };
  const leaveSaveThenGo = async () => {
    if (!validate()) return;
    setLeavePromptOpen(false);
    if (savedEntryId) await saveChanges();
    else await saveNew();
    nav("/master-order");
  };

  // ── attach to Order (Sprint Product-Cost-Attach) ─────────────────────
  // A snapshot needs a saved Workbook entry — attaching a non-persisted
  // draft would leave workbook_entry_id null and defeat the point of the
  // link. If dirty, the user is prompted to save first (using the same
  // path they'd hit via the top-bar save button).
  const buildSnapshotPayload = (orderId) => ({
    order_id: orderId,
    workbook_entry_id: savedEntryId,
    title: (productName || "").trim() || "Изделие",
    currency_code: currency,
    full_cost_total: Number.isFinite(fullCostTotal) ? fullCostTotal : null,
    sale_price: hasSalePrice ? salePriceNum : null,
    profit: profit,
    margin_percent: marginPercent,
  });

  const openAttachPicker = async () => {
    if (!validate() || saving) return;
    if (!savedEntryId) {
      // First save silently so a workbook_entry_id exists to link to.
      await saveNew();
      // Save toasts on its own; if it failed, savedEntryId stays null.
      return;
    }
    if (dirty) {
      await saveChanges();
      if (dirty) return; // save failed
    }
    setAttachPickerOpen(true);
  };

  const attachToOrder = async (order) => {
    try {
      const snap = await createProductCostSnapshot(buildSnapshotPayload(order.id));
      toast.success(`Добавлено в заказ №${order.order_number}`);
      setAttachPickerOpen(false);
      return snap;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && detail.code === "snapshot_already_exists") {
        setAttachPickerOpen(false);
        setAttachConfirm({ order, existing: detail.existing });
        return null;
      }
      toast.error(formatApiError(e) || "Не удалось добавить в заказ");
      return null;
    }
  };

  const confirmUpdateSnapshot = async () => {
    if (!attachConfirm) return;
    const { order, existing } = attachConfirm;
    try {
      await updateProductCostSnapshot(existing.id, buildSnapshotPayload(order.id));
      toast.success(`Обновлено в заказе №${order.order_number}`);
      setAttachConfirm(null);
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось обновить расчёт в заказе");
    }
  };

  if (accessLoading) return <MOShell active="product-cost" title="Себестоимость изделия"><LoadingBlock /></MOShell>;
  if (mode === "no_access") return <NoAccessScreen />;

  return (
    <MOShell active="product-cost" title="Себестоимость изделия"
             subtitle="Компоненты будущего готового изделия — этап 1.">
      <div data-testid="mo-product-cost"
           className="pb-24 max-w-2xl overflow-x-clip"
           style={{ overscrollBehaviorY: "contain" }}>
        {loadingEntry && (
          <div className="mb-3 text-[13px]" style={{ color: "#8B6F4B" }}>Загружаем расчёт…</div>
        )}

        {orderCtxId && (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
               style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-pc-order-context">
            <div className="text-[12.5px]" style={{ color: "#5B4326" }}>
              Открыто из заказа № {orderCtxNumber || "…"}
            </div>
            <button onClick={() => nav(`/master-order/orders/${orderCtxId}`)}
                    className="h-8 px-3 rounded-full text-[12px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-pc-back-to-order">
              К заказу
            </button>
          </div>
        )}

        {/* Top bar: back + save actions */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={doBack}
                  className="h-9 px-3 rounded-full text-[12.5px] inline-flex items-center gap-1"
                  style={{ background: "#F7F1E7", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-pc-back">
            <ArrowLeft size={13} />
            Назад
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            {savedEntryId && dirty && (
              <span className="text-[11px]" style={{ color: "#B26B4A" }}
                    data-testid="mo-pc-dirty-flag">
                Есть несохранённые изменения
              </span>
            )}
            {!savedEntryId ? (
              <button onClick={saveNew} disabled={saving}
                      className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-pc-save">
                <Save size={13} />
                {saving ? "Сохраняем…" : "Сохранить в Рабочую тетрадь"}
              </button>
            ) : (
              <button onClick={saveChanges} disabled={saving || !dirty}
                      className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-pc-save-changes">
                <Save size={13} />
                {saving ? "Сохраняем…" : "Сохранить изменения"}
              </button>
            )}
            <button onClick={openAttachPicker} disabled={saving}
                    className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                    style={{ background: "#F7F1E7", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #C7A574" }}
                    data-testid="mo-pc-attach-to-order">
              <PackagePlus size={13} />
              Добавить в заказ
            </button>
          </div>
        </div>
        {/* Product title + currency */}
        <div className="rounded-2xl p-4 md:p-5 mb-3"
             style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
            Название изделия
          </div>
          <input value={productName}
                 onChange={(e) => setProductName(e.target.value.slice(0, 200))}
                 placeholder="Торт Шоколад · Вишня"
                 className="w-full h-10 px-3 rounded-md bg-white text-base outline-none"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-pc-title" />
          <div className="mt-3 flex items-center gap-2">
            <div className="text-[11px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Валюта</div>
            <div className="inline-flex rounded-full p-0.5"
                 style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-pc-currency-switch">
              {CURRENCIES.map((c) => (
                <button key={c} onClick={() => setCurrency(c)}
                        className="h-7 px-3 rounded-full text-[12px]"
                        style={{
                          background: currency === c ? "#4A3728" : "transparent",
                          color: currency === c ? "#F7EFE0" : "#4A3728",
                        }}
                        data-testid={`mo-pc-currency-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Components section */}
        <div className="text-[10.5px] tracking-[0.16em] uppercase mb-2"
             style={{ color: "#8B6F4B" }}>Компоненты изделия</div>

        {components.length === 0 && (
          <div className="rounded-2xl p-4 text-[13px] mb-3"
               style={{ background: "#F7F1E7", color: "#5B4326",
                        boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-pc-empty">
            Пока ни одного компонента. Добавьте бисквит, крем, начинку — из Рабочей тетради или вручную.
          </div>
        )}

        {components.length > 0 && (
          <div className="space-y-2 mb-3" data-testid="mo-pc-components">
            {components.map((c) => (
              <ComponentRow key={c.id}
                            comp={c}
                            productCurrency={currency}
                            onPatch={(patch) => patchComponent(c.id, patch)}
                            onRemove={() => removeComponent(c.id)} />
            ))}
          </div>
        )}

        <button onClick={openAdd}
                className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-1.5"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="mo-pc-add">
          <Plus size={13} />
          Добавить компонент
        </button>

        {/* ---- Packaging & consumables (Stage 2) --------------------------- */}
        <div className="text-[10.5px] tracking-[0.16em] uppercase mb-2 mt-6"
             style={{ color: "#8B6F4B" }}>Упаковка и расходники</div>

        {packagingRows.length === 0 && (
          <div className="rounded-2xl p-4 text-[13px] mb-3"
               style={{ background: "#F7F1E7", color: "#5B4326",
                        boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-pc-pkg-empty">
            Позиций упаковки пока нет. Возьмите из справочника «Упаковка» или добавьте вручную.
          </div>
        )}

        {packagingRows.length > 0 && (
          <div className="space-y-2 mb-3" data-testid="mo-pc-packaging">
            {packagingRows.map((r) => (
              <PackagingRow key={r.id}
                            row={r}
                            productCurrency={currency}
                            onPatch={(patch) => patchPkg(r.id, patch)}
                            onRemove={() => removePkg(r.id)} />
            ))}
          </div>
        )}

        <button onClick={() => { setPkgAddMode("choose"); setPkgAddOpen(true); }}
                className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-1.5"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="mo-pc-pkg-add">
          <Plus size={13} />
          Добавить
        </button>

        {/* ---- Labor (Stage 3, optional) ---------------------------------- */}
        <div className="text-[10.5px] tracking-[0.16em] uppercase mb-2 mt-6"
             style={{ color: "#8B6F4B" }}>Труд</div>
        <div className="rounded-2xl p-4 md:p-5 mb-3 bg-white"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-pc-labor">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Часы</div>
              <input value={laborHours} type="text" inputMode="decimal"
                     onChange={(e) => setLaborHours(e.target.value)}
                     placeholder="—"
                     className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-pc-labor-hours" />
            </div>
            <div>
              <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
                Ставка / час, {currency}
              </div>
              <input value={hourlyRate} type="text" inputMode="decimal"
                     onChange={(e) => setHourlyRate(e.target.value)}
                     placeholder="—"
                     className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-pc-labor-rate" />
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#EFE4D0] flex items-baseline justify-between">
            <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Итог по труду</div>
            <div className="text-[14px] tabular-nums"
                 style={{ color: laborTotal === null ? "#B9A692" : "#2A1D14" }}
                 data-testid="mo-pc-labor-total">
              {laborTotal === null ? "Труд не включён в расчёт" : `${fmtDec(laborTotal)} ${currency}`}
            </div>
          </div>
        </div>

        {/* ---- Additional expenses (Stage 3, manual only) ----------------- */}
        <div className="text-[10.5px] tracking-[0.16em] uppercase mb-2 mt-6"
             style={{ color: "#8B6F4B" }}>Дополнительные расходы</div>
        {expenses.length === 0 && (
          <div className="rounded-2xl p-4 text-[13px] mb-3"
               style={{ background: "#F7F1E7", color: "#5B4326",
                        boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-pc-expenses-empty">
            Ягоды для декора, доставка ингредиентов, электричество и т.п. — по одной строке.
          </div>
        )}
        {expenses.length > 0 && (
          <div className="space-y-2 mb-3" data-testid="mo-pc-expenses">
            {expenses.map((e) => (
              <ExpenseRow key={e.id}
                          row={e}
                          currency={currency}
                          onPatch={(patch) => setExpenses((prev) => prev.map((x) => x.id === e.id ? { ...x, ...patch } : x))}
                          onRemove={() => setExpenses((prev) => prev.filter((x) => x.id !== e.id))} />
            ))}
          </div>
        )}
        <button onClick={() => setExpenses((prev) => [...prev, { id: uid(), name: "", amount: null, currency }])}
                className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-1.5"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="mo-pc-expense-add">
          <Plus size={13} />
          Добавить расход
        </button>

        {/* ---- Sale price (Stage 4, optional) ----------------------------- */}
        <div className="text-[10.5px] tracking-[0.16em] uppercase mb-2 mt-6"
             style={{ color: "#8B6F4B" }}>Цена продажи</div>
        <div className="rounded-2xl p-4 md:p-5 mb-3 bg-white"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-pc-sale">
          <div className="grid grid-cols-[minmax(0,1fr)_120px] items-center gap-2">
            <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
              Ваша цена продажи, {currency}
            </div>
            <input value={salePrice} type="text" inputMode="decimal"
                   onChange={(e) => setSalePrice(e.target.value)}
                   placeholder="—"
                   className="h-10 px-3 rounded-md bg-white text-base outline-none text-right tabular-nums min-w-0"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-pc-sale-price" />
          </div>
          {salePrice !== "" && salePriceNum === null && (
            <div className="mt-2 text-[11.5px]" style={{ color: "#B26B4A" }}
                 data-testid="mo-pc-sale-warn-invalid">
              Укажите число — например, 100 или 79,50.
            </div>
          )}
          {salePriceNum !== null && salePriceNum <= 0 && (
            <div className="mt-2 text-[11.5px]" style={{ color: "#B26B4A" }}
                 data-testid="mo-pc-sale-warn-nonpositive">
              Цена должна быть больше нуля, чтобы посчитать маржу.
            </div>
          )}
          {profit !== null && profit < 0 && (
            <div className="mt-2 text-[11.5px]" style={{ color: "#B26B4A" }}
                 data-testid="mo-pc-sale-warn-loss">
              Цена продажи ниже себестоимости — прибыль отрицательная.
            </div>
          )}
        </div>

        {/* Bottom subtotals + full cost total */}
        <div className="mt-6 rounded-2xl p-4"
             style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <div className="flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Себестоимость компонентов</div>
            <div className="text-[16px] tabular-nums" style={{ color: "#2A1D14" }}
                 data-testid="mo-pc-subtotal">{fmtDec(subtotal)} {currency}</div>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Упаковка и расходники</div>
            <div className="text-[16px] tabular-nums" style={{ color: "#2A1D14" }}
                 data-testid="mo-pc-pkg-subtotal">{fmtDec(packagingSubtotal)} {currency}</div>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Труд</div>
            <div className="text-[16px] tabular-nums"
                 style={{ color: laborTotal === null ? "#B9A692" : "#2A1D14" }}
                 data-testid="mo-pc-labor-subtotal">
              {laborTotal === null ? "—" : `${fmtDec(laborTotal)} ${currency}`}
            </div>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Дополнительные расходы</div>
            <div className="text-[16px] tabular-nums" style={{ color: "#2A1D14" }}
                 data-testid="mo-pc-expenses-subtotal">{fmtDec(expensesSubtotal)} {currency}</div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#EFE4D0] flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Полная себестоимость изделия</div>
            <div className="text-[20px] tabular-nums"
                 style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
                 data-testid="mo-pc-full-total">
              {fmtDec(fullCostTotal)} {currency}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#EFE4D0] flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Цена продажи</div>
            <div className="text-[16px] tabular-nums"
                 style={{ color: hasSalePrice ? "#2A1D14" : "#B9A692" }}
                 data-testid="mo-pc-sale-summary">
              {hasSalePrice ? `${fmtDec(salePriceNum)} ${currency}` : "—"}
            </div>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Прибыль с изделия</div>
            <div className="text-[16px] tabular-nums"
                 style={{
                   color: profit === null ? "#B9A692"
                        : profit < 0 ? "#B26B4A" : "#2A6B48",
                 }}
                 data-testid="mo-pc-profit">
              {profit === null ? "—" : `${fmtDec(profit)} ${currency}`}
            </div>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-[13px]" style={{ color: "#5B4326" }}>Маржа</div>
            <div className="text-[16px] tabular-nums"
                 style={{
                   color: marginPercent === null ? "#B9A692"
                        : marginPercent < 0 ? "#B26B4A" : "#2A6B48",
                 }}
                 data-testid="mo-pc-margin">
              {marginPercent === null ? "—" : `${fmtDec(marginPercent)}%`}
            </div>
          </div>
          {(components.some((c) => !isUsable(c, currency) || componentTotal(c) === null)
            || packagingRows.some((r) => !isPkgUsable(r, currency) || pkgLineTotal(r) === null)
            || expenses.some((e) => !Number.isFinite(e.amount) || e.amount === null || (e.name || "").trim() === "")) && (
            <div className="mt-2 text-[11.5px]" style={{ color: "#B26B4A" }}
                 data-testid="mo-pc-excluded-hint">
              В итог не включены позиции без цены/суммы или в другой валюте.
            </div>
          )}
        </div>
      </div>

      {addOpen && (
        <AddComponentModal
          mode={addMode}
          setMode={setAddMode}
          onClose={closeAdd}
          onAdd={addComponent}
          productCurrency={currency}
        />
      )}

      {pkgAddOpen && (
        <AddPackagingModal
          mode={pkgAddMode}
          setMode={setPkgAddMode}
          onClose={() => { setPkgAddOpen(false); setPkgAddMode(null); }}
          onAdd={addPkg}
          productCurrency={currency}
        />
      )}

      {leavePromptOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
             style={{ background: "rgba(35,23,16,0.55)" }}
             onClick={(e) => { if (e.target === e.currentTarget) setLeavePromptOpen(false); }}
             data-testid="mo-pc-leave-prompt">
          <div className="w-full max-w-sm bg-[#FBF5EA] rounded-2xl p-5"
               style={{ boxShadow: "0 8px 32px rgba(35,23,16,0.15)", maxHeight: "90dvh", overflowY: "auto" }}>
            <div className="text-[16px] mb-2"
                 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Есть несохранённые изменения. Сохранить расчёт перед выходом?
            </div>
            <div className="mt-3 flex flex-wrap gap-2 justify-end">
              <button onClick={() => setLeavePromptOpen(false)}
                      className="h-9 px-3 rounded-full text-[12.5px]"
                      style={{ background: "transparent", color: "#4A3728",
                               boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-pc-leave-cancel">
                Отмена
              </button>
              <button onClick={leaveDiscard}
                      className="h-9 px-3 rounded-full text-[12.5px]"
                      style={{ background: "#F7F1E7", color: "#4A3728",
                               boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-pc-leave-discard">
                Выйти без сохранения
              </button>
              <button onClick={leaveSaveThenGo} disabled={saving}
                      className="h-9 px-3.5 rounded-full text-[12.5px] disabled:opacity-60"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-pc-leave-save">
                {saving ? "Сохраняем…" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {attachPickerOpen && (
        <OrderPickerModal
          onClose={() => setAttachPickerOpen(false)}
          onPick={attachToOrder}
        />
      )}

      {attachConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
             style={{ background: "rgba(35,23,16,0.55)" }}
             onClick={(e) => { if (e.target === e.currentTarget) setAttachConfirm(null); }}
             data-testid="mo-pc-attach-confirm">
          <div className="w-full max-w-sm bg-[#FBF5EA] rounded-2xl p-5"
               style={{ boxShadow: "0 8px 32px rgba(35,23,16,0.15)", maxHeight: "90dvh", overflowY: "auto" }}>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
              Заказ №{attachConfirm.order.order_number}
            </div>
            <div className="text-[17px] mb-2"
                 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Этот расчёт уже добавлен в заказ. Обновить сохранённые данные?
            </div>
            <p className="text-[12.5px] mb-3" style={{ color: "#5B4326" }}>
              Данные в заказе будут заменены на текущие значения расчёта.
              Прежний snapshot восстановить будет нельзя.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 justify-end">
              <button onClick={() => setAttachConfirm(null)}
                      className="h-9 px-3 rounded-full text-[12.5px]"
                      style={{ background: "transparent", color: "#4A3728",
                               boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-pc-attach-confirm-cancel">
                Отмена
              </button>
              <button onClick={confirmUpdateSnapshot}
                      className="h-9 px-3.5 rounded-full text-[12.5px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-pc-attach-confirm-update">
                Обновить
              </button>
            </div>
          </div>
        </div>
      )}
    </MOShell>
  );
}

/* ─────────── Order picker modal (Sprint Product-Cost-Attach) ───────────
 * Compact drawer listing existing user orders. Newest first, capped at 200
 * (matches backend list_orders default). No order creation — this sprint
 * is explicitly about attaching to existing orders. */
function OrderPickerModal({ onClose, onPick }) {
  const [orders, setOrders] = useState(null);
  const [clientMap, setClientMap] = useState({});
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [os, cs] = await Promise.all([
          listOrders().catch(() => []),
          listClients().catch(() => []),
        ]);
        if (cancelled) return;
        setOrders(os || []);
        setClientMap(Object.fromEntries((cs || []).map((c) => [c.id, c])));
      } catch { if (!cancelled) setOrders([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!orders) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => {
      const c = clientMap[o.client_id];
      return (
        String(o.order_number || "").toLowerCase().includes(needle)
        || (o.event_type || "").toLowerCase().includes(needle)
        || (c?.name || "").toLowerCase().includes(needle)
      );
    });
  }, [orders, q, clientMap]);

  const pick = async (o) => {
    if (busyId) return;
    setBusyId(o.id);
    try { await onPick(o); }
    finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.55)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-pc-order-picker">
      <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
            <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Выберите заказ
            </div>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-pc-order-picker-close">
            <X size={13} />
          </button>
        </div>
        <div className="flex-shrink-0 px-5 md:px-6 pb-3">
          <input value={q}
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Поиск по номеру, клиенту или событию"
                 className="w-full h-10 px-3 rounded-md bg-white text-base outline-none"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-pc-order-picker-search" />
        </div>
        <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto space-y-1.5">
          {orders === null && (
            <div className="text-[13px] py-3" style={{ color: "#8B6F4B" }}>Загружаем…</div>
          )}
          {orders !== null && filtered.length === 0 && (
            <div className="rounded-lg p-3 text-center"
                 style={{ background: "#F7F1E7", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-pc-order-picker-empty">
              <div className="text-[13.5px]">Заказы не найдены</div>
              <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                Создайте заказ в разделе «Заказы», затем вернитесь сюда.
              </div>
            </div>
          )}
          {filtered && filtered.map((o) => {
            const c = clientMap[o.client_id];
            const eventDate = o.event_date
              ? new Date(o.event_date).toLocaleDateString("ru-RU",
                  { day: "numeric", month: "long", year: "numeric" })
              : "";
            return (
              <button key={o.id}
                      onClick={() => pick(o)}
                      disabled={busyId === o.id}
                      className="w-full text-left rounded-lg p-3 bg-white disabled:opacity-60"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid={`mo-pc-order-pick-${o.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[14px]" style={{ color: "#2A1D14" }}>
                    Заказ №{o.order_number}
                  </div>
                  <div className="text-[11.5px] tabular-nums whitespace-nowrap" style={{ color: "#8B6F4B" }}>
                    {eventDate}
                  </div>
                </div>
                <div className="text-[12px] mt-0.5 truncate" style={{ color: "#5B4326" }}>
                  {c?.name || "Клиент не указан"}
                  {o.event_type ? ` · ${o.event_type}` : ""}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─────────── component row ─────────── */
const QUICK_COEFS = [0.5, 1, 1.5, 2];

function ComponentRow({ comp, productCurrency, onPatch, onRemove }) {
  const noCost = !Number.isFinite(comp.base_cost) || comp.base_cost === null;
  const currencyMismatch = !noCost && comp.currency !== productCurrency;
  const total = componentTotal(comp);
  const [customOpen, setCustomOpen] = useState(!QUICK_COEFS.includes(Number(comp.coefficient)));

  return (
    <div className="rounded-xl p-3 md:p-4 bg-white"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-pc-component-row">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}
               data-testid="mo-pc-component-name">
            {comp.name || "Без названия"}
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
            {comp.source === "workbook" ? "Из Рабочей тетради" : "Вручную"}
          </div>
        </div>
        <button onClick={onRemove}
                aria-label="Удалить компонент"
                className="w-8 h-8 rounded-full grid place-items-center flex-shrink-0"
                style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-pc-component-remove">
          <Trash2 size={13} />
        </button>
      </div>

      {/* base cost */}
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_80px] items-center gap-2">
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Базовая себестоимость</div>
        {comp.source === "manual" ? (
          <input type="text" inputMode="decimal"
                 value={comp.base_cost ?? ""}
                 onChange={(e) => onPatch({ base_cost: parseAmount(e.target.value) })}
                 placeholder="—"
                 className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-pc-component-base-cost" />
        ) : (
          <div className="text-right text-[13px] tabular-nums whitespace-nowrap"
               style={{ color: noCost ? "#B26B4A" : "#2A1D14" }}
               data-testid="mo-pc-component-base-cost-value">
            {noCost ? "не рассчитана" : `${fmtDec(comp.base_cost)} ${comp.currency}`}
          </div>
        )}
      </div>

      {/* status hints (rule 5 + 6) */}
      {noCost && (
        <div className="mt-1 text-[11.5px]" style={{ color: "#B26B4A" }}
             data-testid="mo-pc-warn-no-cost">
          Себестоимость не рассчитана — компонент не включён в итог.
        </div>
      )}
      {currencyMismatch && (
        <div className="mt-1 text-[11.5px]" style={{ color: "#B26B4A" }}
             data-testid="mo-pc-warn-currency">
          Валюта компонента ({comp.currency}) не совпадает с {productCurrency} — не включён в итог.
        </div>
      )}

      {/* coefficient */}
      <div className="mt-3">
        <div className="text-[11.5px] mb-1" style={{ color: "#8B6F4B" }}>Коэффициент использования</div>
        <div className="flex flex-wrap items-center gap-1">
          {QUICK_COEFS.map((k) => (
            <button key={k} onClick={() => { onPatch({ coefficient: k }); setCustomOpen(false); }}
                    className="h-8 px-2.5 rounded-full text-[12px]"
                    style={{
                      background: Number(comp.coefficient) === k && !customOpen ? "#4A3728" : "#F7F1E7",
                      color: Number(comp.coefficient) === k && !customOpen ? "#F7EFE0" : "#4A3728",
                      boxShadow: Number(comp.coefficient) === k && !customOpen ? "none" : "inset 0 0 0 1px #EFE4D0",
                    }}
                    data-testid={`mo-pc-coef-${k}`}>
              ×{fmtDec(k, k % 1 === 0 ? 0 : 1)}
            </button>
          ))}
          <button onClick={() => setCustomOpen(true)}
                  className="h-8 px-2.5 rounded-full text-[12px] inline-flex items-center gap-1"
                  style={{
                    background: customOpen ? "#4A3728" : "#F7F1E7",
                    color: customOpen ? "#F7EFE0" : "#4A3728",
                    boxShadow: customOpen ? "none" : "inset 0 0 0 1px #EFE4D0",
                  }}
                  data-testid="mo-pc-coef-custom-open">
            <Pencil size={11} /> Свой
          </button>
        </div>
        {customOpen && (
          <div className="mt-2 flex items-center gap-2">
            <CustomCoefInput coefficient={comp.coefficient}
                             onChange={(v) => onPatch({ coefficient: v })} />
            <span className="text-[11px]" style={{ color: "#8B6F4B" }}>×</span>
          </div>
        )}
      </div>

      {/* row total */}
      <div className="mt-3 pt-3 border-t border-[#EFE4D0] flex items-baseline justify-between">
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Итог по компоненту</div>
        <div className="text-[14px] tabular-nums"
             style={{ color: (isUsable(comp, productCurrency) && total !== null) ? "#2A1D14" : "#B9A692" }}
             data-testid="mo-pc-component-total">
          {total === null || !isUsable(comp, productCurrency)
            ? "—"
            : `${fmtDec(total)} ${comp.currency}`}
        </div>
      </div>
    </div>
  );
}

/* ─────────── custom coefficient input ───────────
 * The parent stores `coefficient` as a Number, but the raw text field needs
 * to hold in-progress strings like "1," or "0,25" while the user is typing.
 * A controlled `value={number}` would strip trailing separators and make
 * decimals impossible to enter. We keep local text state, parse on each
 * keystroke, and only re-sync from the outside when the numeric coefficient
 * changes for a reason other than our own typing (e.g. a preset chip is
 * clicked). Empty / partial / zero input propagates `0` upward so the row
 * total renders as "—" but a valid > 0 number updates parent immediately —
 * which makes the row and product subtotal reflect the new coefficient in
 * the same render.  Accepts both comma AND dot separators. */
function CustomCoefInput({ coefficient, onChange }) {
  const initial = coefficient === null || coefficient === undefined || coefficient === ""
    ? ""
    : String(coefficient).replace(".", ",");
  const [text, setText] = useState(initial);
  const lastExternalRef = React.useRef(coefficient);

  useEffect(() => {
    // Re-sync when the value changed OUTSIDE of our typing (preset chip).
    if (coefficient !== lastExternalRef.current) {
      const localParsed = Number(String(text).replace(",", "."));
      if (localParsed !== coefficient) {
        setText(coefficient === null || coefficient === undefined || coefficient === ""
          ? "" : String(coefficient).replace(".", ","));
      }
      lastExternalRef.current = coefficient;
    }
  }, [coefficient, text]);

  const handle = (e) => {
    // Allow only digits + one comma/dot. Everything else is dropped so the
    // user cannot accidentally paste letters, minus signs, or a second
    // separator.
    let raw = e.target.value.replace(/[^\d.,]/g, "");
    // Collapse runs of separators to a single one, then keep only the first.
    const parts = raw.split(/[.,]/);
    if (parts.length > 2) raw = parts[0] + "," + parts.slice(1).join("");
    setText(raw);
    // Parse for the parent. Empty or trailing-separator input → 0.
    const normalized = raw.replace(",", ".");
    const n = Number(normalized);
    const next = Number.isFinite(n) && n > 0 ? n : 0;
    lastExternalRef.current = next;
    onChange(next);
  };

  return (
    <input type="text" inputMode="decimal"
           value={text}
           onChange={handle}
           placeholder="Напр. 0,75"
           className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none w-28 tabular-nums"
           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
           data-testid="mo-pc-coef-custom-input" />
  );
}

/* ─────────── modal: add component ─────────── */
function AddComponentModal({ mode, setMode, onClose, onAdd, productCurrency }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-pc-add-modal">
      <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between">
          <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {mode === "workbook" ? "Из Рабочей тетради"
              : mode === "manual" ? "Добавить вручную"
              : "Добавить компонент"}
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-pc-add-close">
            <X size={13} />
          </button>
        </div>

        {mode === "choose" && (
          <div className="p-5 md:p-6 pt-3 space-y-2" data-testid="mo-pc-add-choose">
            <button onClick={() => setMode("workbook")}
                    className="w-full text-left rounded-xl bg-white p-4"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-pc-add-from-workbook">
              <div className="text-[15px] flex items-center gap-1.5" style={{ color: "#2A1D14" }}>
                <NotebookPen size={14} /> Из Рабочей тетради
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Взять сохранённый рецепт как компонент.
              </div>
            </button>
            <button onClick={() => setMode("manual")}
                    className="w-full text-left rounded-xl bg-white p-4"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-pc-add-manual">
              <div className="text-[15px] flex items-center gap-1.5" style={{ color: "#2A1D14" }}>
                <Pencil size={14} /> Добавить вручную
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Название и базовая себестоимость.
              </div>
            </button>
          </div>
        )}

        {mode === "workbook" && (
          <WorkbookPicker onAdd={onAdd} productCurrency={productCurrency} />
        )}

        {mode === "manual" && (
          <ManualAddForm onAdd={onAdd} productCurrency={productCurrency} />
        )}
      </div>
    </div>
  );
}

/* ─────────── manual add form ─────────── */
function ManualAddForm({ onAdd, productCurrency }) {
  const [name, setName] = useState("");
  const [base, setBase] = useState("");

  const submit = () => {
    const trimmed = name.trim();
    const cost = parseAmount(base);
    if (!trimmed) { toast.error("Укажите название компонента"); return; }
    if (cost === null || cost < 0) { toast.error("Укажите базовую себестоимость"); return; }
    onAdd({
      id: uid(),
      source: "manual",
      name: trimmed,
      base_cost: cost,
      currency: productCurrency,
      coefficient: 1,
    });
  };

  return (
    <div className="p-5 md:p-6 pt-3" data-testid="mo-pc-manual-form">
      <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Название</div>
      <input value={name}
             onChange={(e) => setName(e.target.value.slice(0, 200))}
             placeholder="Крем шоколадный"
             className="w-full h-10 px-3 rounded-md bg-white text-base outline-none"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
             data-testid="mo-pc-manual-name" />
      <div className="text-[10.5px] tracking-[0.16em] uppercase mt-3 mb-1" style={{ color: "#8B6F4B" }}>
        Базовая себестоимость, {productCurrency}
      </div>
      <input value={base} type="text" inputMode="decimal"
             onChange={(e) => setBase(e.target.value)}
             placeholder="4,38"
             className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
             data-testid="mo-pc-manual-base" />
      <button onClick={submit}
              className="mt-4 h-10 px-4 rounded-full text-[13px]"
              style={{ background: "#4A3728", color: "#F7EFE0" }}
              data-testid="mo-pc-manual-submit">
        Добавить компонент
      </button>
    </div>
  );
}

/* ─────────── workbook picker ─────────── */
function WorkbookPicker({ onAdd, productCurrency }) {
  const [entries, setEntries] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const rows = await listWorkbookEntries({ type: "recipe_calculation" });
      setEntries(rows || []);
    } catch { setEntries([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const full = await getWorkbookEntry(id);
      const p = full?.payload || {};
      const cost = p.cost || null;
      // Prefer full_total when the ingredient cost was complete.
      // Otherwise we treat it as "not yet calculated" — the row will show up
      // in the product list with a warning and be excluded from the subtotal.
      let base = null;
      if (cost && cost.complete === true && cost.full_total !== null && cost.full_total !== undefined) {
        base = Number(cost.full_total);
      }
      const compCurrency = (cost && cost.currency_code)
        || p.currency_code
        || productCurrency;
      onAdd({
        id: uid(),
        source: "workbook",
        workbook_entry_id: full.id,
        name: (p.title || full.title || "Компонент").trim() || "Компонент",
        base_cost: Number.isFinite(base) ? base : null,
        currency: compCurrency,
        coefficient: 1,
      });
    } catch (e) {
      toast.error("Не удалось загрузить рецепт");
    } finally { setBusyId(null); }
  };

  return (
    <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto"
         data-testid="mo-pc-workbook-picker">
      {entries === null && <div className="text-[13px] py-4" style={{ color: "#8B6F4B" }}>Загружаем…</div>}
      {entries !== null && entries.length === 0 && (
        <div className="rounded-lg p-3 text-center"
             style={{ background: "#F7F1E7", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-pc-workbook-empty">
          <div className="text-[13.5px]">В Рабочей тетради пока нет сохранённых рецептов</div>
          <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
            Сохраните рецепт в «Пересчитать рецепт» — он появится здесь.
          </div>
        </div>
      )}
      {entries && entries.length > 0 && (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <button key={e.id}
                    onClick={() => pick(e.id)}
                    disabled={busyId === e.id}
                    className="w-full text-left rounded-lg p-3 bg-white disabled:opacity-60"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid={`mo-pc-workbook-item-${e.id}`}>
              <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>
                {e.title || "Рецепт"}
              </div>
              <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Обновлено {new Date(e.updated_at).toLocaleDateString("ru-RU")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── packaging row (Stage 2) ─────────── */
function PackagingRow({ row, productCurrency, onPatch, onRemove }) {
  const noPrice = !Number.isFinite(row.unit_cost) || row.unit_cost === null;
  const currencyMismatch = !noPrice && row.currency !== productCurrency;
  const total = pkgLineTotal(row);
  const usable = isPkgUsable(row, productCurrency);
  const isCatalog = row.source === "packaging";

  return (
    <div className="rounded-xl p-3 md:p-4 bg-white"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-pc-pkg-row">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}
               data-testid="mo-pc-pkg-name">
            {row.name || "Без названия"}
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
            {isCatalog ? "Из справочника «Упаковка»" : "Вручную"}
          </div>
        </div>
        <button onClick={onRemove}
                aria-label="Удалить позицию"
                className="w-8 h-8 rounded-full grid place-items-center flex-shrink-0"
                style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-pc-pkg-remove">
          <Trash2 size={13} />
        </button>
      </div>

      {/* quantity + unit */}
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_80px_56px] items-center gap-2">
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Количество</div>
        <input type="text" inputMode="decimal"
               value={row.quantity ?? ""}
               onChange={(e) => onPatch({ quantity: parseAmount(e.target.value) ?? 0 })}
               placeholder="1"
               className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid="mo-pc-pkg-quantity" />
        {isCatalog ? (
          <div className="text-[12px] text-center" style={{ color: "#5B4326" }}
               data-testid="mo-pc-pkg-unit-static">{row.unit}</div>
        ) : (
          <select value={row.unit}
                  onChange={(e) => onPatch({ unit: e.target.value })}
                  className="h-9 px-1 rounded-md bg-white text-base md:text-[12px] outline-none min-w-0"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-pc-pkg-unit">
            {MANUAL_PKG_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        )}
      </div>

      {/* unit cost */}
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_80px] items-center gap-2">
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Цена за единицу</div>
        <input type="text" inputMode="decimal"
               value={row.unit_cost ?? ""}
               onChange={(e) => onPatch({ unit_cost: parseAmount(e.target.value) })}
               placeholder="—"
               className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid="mo-pc-pkg-unit-cost" />
      </div>

      {/* warnings */}
      {noPrice && (
        <div className="mt-1 text-[11.5px]" style={{ color: "#B26B4A" }}
             data-testid="mo-pc-pkg-warn-no-price">
          Цена не указана — позиция не включена в итог.
        </div>
      )}
      {currencyMismatch && (
        <div className="mt-1 text-[11.5px]" style={{ color: "#B26B4A" }}
             data-testid="mo-pc-pkg-warn-currency">
          Валюта позиции ({row.currency}) не совпадает с {productCurrency} — не включена в итог.
        </div>
      )}

      {/* row total */}
      <div className="mt-3 pt-3 border-t border-[#EFE4D0] flex items-baseline justify-between">
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Итог по позиции</div>
        <div className="text-[14px] tabular-nums"
             style={{ color: usable && total !== null ? "#2A1D14" : "#B9A692" }}
             data-testid="mo-pc-pkg-line-total">
          {total === null || !usable
            ? "—"
            : `${fmtDec(total)} ${row.currency}`}
        </div>
      </div>
    </div>
  );
}

/* ─────────── add packaging modal ─────────── */
function AddPackagingModal({ mode, setMode, onClose, onAdd, productCurrency }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-pc-pkg-modal">
      <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between">
          <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {mode === "catalog" ? "Из справочника" : mode === "manual" ? "Добавить вручную" : "Добавить позицию"}
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-pc-pkg-modal-close">
            <X size={13} />
          </button>
        </div>

        {mode === "choose" && (
          <div className="p-5 md:p-6 pt-3 space-y-2" data-testid="mo-pc-pkg-choose">
            <button onClick={() => setMode("catalog")}
                    className="w-full text-left rounded-xl bg-white p-4"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-pc-pkg-from-catalog">
              <div className="text-[15px] flex items-center gap-1.5" style={{ color: "#2A1D14" }}>
                <NotebookPen size={14} /> Из справочника «Упаковка»
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Взять коробку, подложку, ленту и т.п. с сохранённой ценой.
              </div>
            </button>
            <button onClick={() => setMode("manual")}
                    className="w-full text-left rounded-xl bg-white p-4"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-pc-pkg-manual-open">
              <div className="text-[15px] flex items-center gap-1.5" style={{ color: "#2A1D14" }}>
                <Pencil size={14} /> Добавить вручную
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Название, количество, единица, цена за единицу.
              </div>
            </button>
          </div>
        )}

        {mode === "catalog" && (
          <CatalogPicker onAdd={onAdd} productCurrency={productCurrency} />
        )}
        {mode === "manual" && (
          <ManualPkgForm onAdd={onAdd} productCurrency={productCurrency} />
        )}
      </div>
    </div>
  );
}

/* ─────────── packaging catalog picker ─────────── */
function CatalogPicker({ onAdd, productCurrency }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const list = await listPackagingItems();
        // Only active items — inactive rows in the catalog are historical.
        setItems((list || []).filter((i) => i.is_active !== false));
      } catch { setItems([]); }
    })();
  }, []);

  const pick = async (item) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      // The prices endpoint returns rows sorted by effective_at desc, so
      // the first entry is the current price. If none exists we still add
      // the row — the user will get the "Цена не указана" warning.
      const prices = await listPackagingPricesByItem(item.id);
      const chosen = (prices || []).find((p) => p.currency_code === productCurrency)
                   || (prices || [])[0]
                   || null;
      onAdd({
        id: uid(),
        source: "packaging",
        packaging_item_id: item.id,
        name: item.name,
        quantity: 1,
        unit: item.base_unit || "pcs",
        unit_cost: chosen ? Number(chosen.unit_cost) : null,
        currency: chosen ? chosen.currency_code : productCurrency,
      });
    } catch (e) {
      toast.error("Не удалось загрузить цену");
    } finally { setBusyId(null); }
  };

  return (
    <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto"
         data-testid="mo-pc-pkg-catalog">
      {items === null && <div className="text-[13px] py-4" style={{ color: "#8B6F4B" }}>Загружаем…</div>}
      {items !== null && items.length === 0 && (
        <div className="rounded-lg p-3 text-center"
             style={{ background: "#F7F1E7", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-pc-pkg-catalog-empty">
          <div className="text-[13.5px]">В справочнике «Упаковка» пока пусто</div>
          <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
            Заведите первые позиции в «Master Order → Упаковка».
          </div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((it) => (
            <button key={it.id}
                    onClick={() => pick(it)}
                    disabled={busyId === it.id}
                    className="w-full text-left rounded-lg p-3 bg-white disabled:opacity-60"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid={`mo-pc-pkg-catalog-item-${it.id}`}>
              <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>{it.name}</div>
              <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                Ед.: {it.base_unit}{it.category ? ` · ${it.category}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── manual packaging form ─────────── */
function ManualPkgForm({ onAdd, productCurrency }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("pcs");
  const [price, setPrice] = useState("");

  const submit = () => {
    const trimmed = name.trim();
    const q = parseAmount(qty);
    const p = parseAmount(price);
    if (!trimmed) { toast.error("Укажите название позиции"); return; }
    if (q === null || q <= 0) { toast.error("Укажите количество"); return; }
    if (p === null || p < 0) { toast.error("Укажите цену за единицу"); return; }
    onAdd({
      id: uid(),
      source: "manual",
      packaging_item_id: null,
      name: trimmed,
      quantity: q,
      unit,
      unit_cost: p,
      currency: productCurrency,
    });
  };

  return (
    <div className="p-5 md:p-6 pt-3" data-testid="mo-pc-pkg-manual-form">
      <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Название</div>
      <input value={name}
             onChange={(e) => setName(e.target.value.slice(0, 200))}
             placeholder="Ацетатная плёнка"
             className="w-full h-10 px-3 rounded-md bg-white text-base outline-none"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
             data-testid="mo-pc-pkg-manual-name" />
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_88px] gap-2">
        <div>
          <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Количество</div>
          <input value={qty} type="text" inputMode="decimal"
                 onChange={(e) => setQty(e.target.value)}
                 placeholder="2"
                 className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-pc-pkg-manual-qty" />
        </div>
        <div>
          <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Единица</div>
          <select value={unit} onChange={(e) => setUnit(e.target.value)}
                  className="w-full h-10 px-2 rounded-md bg-white text-base outline-none"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-pc-pkg-manual-unit">
            {MANUAL_PKG_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div className="text-[10.5px] tracking-[0.16em] uppercase mt-3 mb-1" style={{ color: "#8B6F4B" }}>
        Цена за единицу, {productCurrency}
      </div>
      <input value={price} type="text" inputMode="decimal"
             onChange={(e) => setPrice(e.target.value)}
             placeholder="0,20"
             className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
             data-testid="mo-pc-pkg-manual-price" />
      <button onClick={submit}
              className="mt-4 h-10 px-4 rounded-full text-[13px]"
              style={{ background: "#4A3728", color: "#F7EFE0" }}
              data-testid="mo-pc-pkg-manual-submit">
        Добавить позицию
      </button>
    </div>
  );
}


/* ─────────── expense row (Stage 3) ─────────── */
function ExpenseRow({ row, currency, onPatch, onRemove }) {
  const noAmount = !Number.isFinite(row.amount) || row.amount === null;
  return (
    <div className="rounded-xl p-3 bg-white"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-pc-expense-row">
      <div className="grid grid-cols-[minmax(0,1fr)_88px_26px] gap-2 items-center">
        <input value={row.name}
               onChange={(e) => onPatch({ name: e.target.value.slice(0, 200) })}
               placeholder="Название"
               className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid="mo-pc-expense-name" />
        <input value={row.amount ?? ""} type="text" inputMode="decimal"
               onChange={(e) => onPatch({ amount: parseAmount(e.target.value) })}
               placeholder="—"
               className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid="mo-pc-expense-amount" />
        <button onClick={onRemove}
                aria-label="Удалить расход"
                className="w-[26px] h-[26px] rounded-full grid place-items-center justify-self-end"
                style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-pc-expense-remove">
          <X size={11} />
        </button>
      </div>
      {noAmount && (
        <div className="mt-1 text-[11.5px]" style={{ color: "#B26B4A" }}
             data-testid="mo-pc-expense-warn">
          Сумма не указана — расход не включён в итог.
        </div>
      )}
      {!noAmount && (
        <div className="mt-1 text-[11.5px] text-right tabular-nums" style={{ color: "#5B4326" }}
             data-testid="mo-pc-expense-line-total">
          {fmtDec(row.amount)} {currency}
        </div>
      )}
    </div>
  );
}

